/* SemBot · client HTTP.
 *
 * Un solo punto di uscita verso il backend. Motivi:
 *  - il token JWT si aggiunge in un posto solo, e il 401 si gestisce in un
 *    posto solo (il frontend attuale lo ripeteva in ogni hook);
 *  - il backend non ha refresh token: alla scadenza c'è solo il rientro, e
 *    scoprirlo dall'unico posto che riceve i 401 evita schermate a metà;
 *  - due chiamate del backend vogliono un body su DELETE, che va scritto a
 *    mano: meglio una volta qui che in ogni chiamante.
 */

import type { PaginaCrud } from './tipi';

/** In sviluppo resta vuoto: il proxy di Vite gira /api verso localhost:3000. */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '') || '/api';

export class ErroreApi extends Error {
  readonly stato: number;
  readonly corpo: unknown;

  constructor(stato: number, messaggio: string, corpo?: unknown) {
    super(messaggio);
    this.name = 'ErroreApi';
    this.stato = stato;
    this.corpo = corpo;
  }

  /** Il token è scaduto o assente: l'unica cura è rientrare. */
  get eNonAutenticato(): boolean {
    return this.stato === 401;
  }

  /** Rete assente o server irraggiungibile: ha senso riprovare. */
  get eDiRete(): boolean {
    return this.stato === 0;
  }
}

/* Il token vive nel provider di autenticazione; qui si registra un lettore per
   non creare una dipendenza circolare fra client e contesto React. */
type LettoreToken = () => string | null;
type GestoreScadenza = () => void;

let leggiToken: LettoreToken = () => null;
let alTokenScaduto: GestoreScadenza = () => {};

export function collegaAutenticazione(lettore: LettoreToken, scadenza: GestoreScadenza): void {
  leggiToken = lettore;
  alTokenScaduto = scadenza;
}

export type Parametri = Record<
  string,
  string | number | boolean | null | undefined | (string | number)[]
>;

/** Costruisce la query string saltando i parametri non valorizzati: un
 *  `?asset=undefined` sul backend diventa il filtro letterale "undefined". */
function query(parametri?: Parametri): string {
  if (!parametri) return '';
  const q = new URLSearchParams();
  for (const [chiave, valore] of Object.entries(parametri)) {
    if (valore == null || valore === '') continue;
    q.set(chiave, Array.isArray(valore) ? valore.join(',') : String(valore));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

interface OpzioniRichiesta {
  parametri?: Parametri;
  corpo?: unknown;
  segnale?: AbortSignal;
}

async function richiesta<T>(
  metodo: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  percorso: string,
  opzioni: OpzioniRichiesta = {},
): Promise<T> {
  const token = leggiToken();
  const intestazioni: Record<string, string> = { Accept: 'application/json' };
  if (token) intestazioni.Authorization = `Bearer ${token}`;
  if (opzioni.corpo !== undefined) intestazioni['Content-Type'] = 'application/json';

  let risposta: Response;
  try {
    risposta = await fetch(`${BASE}${percorso}${query(opzioni.parametri)}`, {
      method: metodo,
      headers: intestazioni,
      body: opzioni.corpo === undefined ? undefined : JSON.stringify(opzioni.corpo),
      signal: opzioni.segnale,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ErroreApi(0, 'Nessuna risposta dal server. Controlla la connessione.');
  }

  if (risposta.status === 401) {
    // Il 401 del login NON e' una sessione scaduta: sono credenziali sbagliate, e non c'e'
    // nessuna sessione da chiudere. Trattarlo come scadenza faceva comparire «Sessione scaduta,
    // rientra per continuare» proprio a chi stava rientrando, che e' il messaggio piu'
    // sconcertante possibile in quel momento.
    if (percorso.startsWith('/auth/login')) {
      throw new ErroreApi(401, 'Nome utente o password non corretti.');
    }
    alTokenScaduto();
    throw new ErroreApi(401, 'Sessione scaduta. Rientra per continuare.');
  }

  /* 204 e i DELETE del CRUD rispondono senza corpo: leggere JSON da un corpo
     vuoto solleverebbe un errore di parsing travestito da errore di rete. */
  const testo = await risposta.text();
  const corpo = testo ? sicuroJson(testo) : null;

  if (!risposta.ok) {
    throw new ErroreApi(risposta.status, messaggioErrore(corpo, risposta), corpo);
  }

  return corpo as T;
}

function sicuroJson(testo: string): unknown {
  try {
    return JSON.parse(testo);
  } catch {
    return testo;
  }
}

function messaggioErrore(corpo: unknown, risposta: Response): string {
  if (corpo && typeof corpo === 'object' && 'message' in corpo) {
    const m = (corpo as { message: unknown }).message;
    if (typeof m === 'string') return m;
    if (Array.isArray(m)) return m.join(' · ');
  }
  if (typeof corpo === 'string' && corpo.trim()) return corpo;
  return `Richiesta non riuscita (${risposta.status}).`;
}

export const api = {
  get: <T>(percorso: string, parametri?: Parametri, segnale?: AbortSignal) =>
    richiesta<T>('GET', percorso, { parametri, segnale }),

  post: <T>(percorso: string, corpo?: unknown, segnale?: AbortSignal) =>
    richiesta<T>('POST', percorso, { corpo, segnale }),

  patch: <T>(percorso: string, corpo?: unknown, segnale?: AbortSignal) =>
    richiesta<T>('PATCH', percorso, { corpo, segnale }),

  put: <T>(percorso: string, corpo?: unknown, segnale?: AbortSignal) =>
    richiesta<T>('PUT', percorso, { corpo, segnale }),

  /** `corpo` serve a `DELETE /historical-data/delete`, che lo richiede. */
  del: <T>(percorso: string, corpo?: unknown, segnale?: AbortSignal) =>
    richiesta<T>('DELETE', percorso, { corpo, segnale }),
};

/* --------------------------------------------------------------------------
 * @nestjsx/crud: la sintassi dei parametri di lista.
 * Cinque risorse (accounts, exchanges, market-watchers, tradebots, wallets)
 * sono generate dalla libreria e usano questa grammatica, non una convenzione
 * REST qualunque. Concentrarla qui evita che ogni chiamante la reinventi.
 * ------------------------------------------------------------------------ */

export interface ParametriCrud {
  /** Campi da restituire. Ridurli è l'unico modo di alleggerire le liste. */
  fields?: string[];
  /** Filtri nella forma `campo||operatore||valore`. */
  filter?: string[];
  /** Ordinamento nella forma `campo,ASC|DESC`. */
  sort?: string[];
  /** Relazioni da caricare: `relazione||campo1,campo2`. */
  join?: string[];
  limit?: number;
  offset?: number;
  page?: number;
}

/* `filter`, `sort` e `join` si RIPETONO nella query string: `URLSearchParams.set`
   li schiaccerebbe a uno solo, quindi qui si usa `append` e la query si
   costruisce a parte invece di passare per `Parametri`. */
export function queryCrud(p?: ParametriCrud): string {
  if (!p) return '';
  const q = new URLSearchParams();
  if (p.fields?.length) q.set('fields', p.fields.join(','));
  p.filter?.forEach((f) => q.append('filter', f));
  p.sort?.forEach((s) => q.append('sort', s));
  p.join?.forEach((j) => q.append('join', j));
  if (p.limit != null) q.set('limit', String(p.limit));
  if (p.offset != null) q.set('offset', String(p.offset));
  if (p.page != null) q.set('page', String(p.page));
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * `getManyBase` risponde con un array nudo senza `limit`/`page` e con un
 * oggetto paginato con essi. Normalizzare le due forme evita che ogni pagina
 * debba indovinare quale ha ricevuto.
 */
export function normalizzaLista<T>(risposta: T[] | PaginaCrud<T>): PaginaCrud<T> {
  if (Array.isArray(risposta)) {
    return {
      data: risposta,
      count: risposta.length,
      total: risposta.length,
      page: 1,
      pageCount: 1,
    };
  }
  return risposta;
}
